import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './App.scss';
import { Switch, Route, useHistory } from 'react-router-dom';
import { useAuth } from '../../providers/Auth';
import Login from '../Login/Login';
import Home from '../Home/Home';
import PrivateRoute from '../../components/PrivateRoute/PrivateRoute';
import { UserRoles } from '../../constants/bindings';
import Navigation from '../../components/Navigation/Navigation';
import PairingPackage from '../PairingPackage/PairingPackage';
import PairingList from '../PairingList/PairingList';
import Footer from '../../components/Footer/Footer';
import DBM from '../DBM/DBM';
import { SearchOptions } from '../../providers/SearchOptions';
import { DBMProvider } from '../../providers/DBM';
import { ProfileProvider } from '../../providers/Profile';
import { PairingProvider } from '../../providers/PairingPackage';
import { GeneralPairingProvider } from '../../providers/GeneralPairing';
import Loader from '../../components/Loader/Loader';
import { PairingByIdProvider } from '../../providers/PairingById';
import { PairingListProvider } from '../../providers/PairingListProvider';
import { PBIProvider } from '../../providers/PowerBiProvider';
import { ColumnPresetsProvider } from '../../providers/ColumnPresets';
import { CollectiveAgreementProvider } from '../../providers/CollectiveAgreement';
import Admin from '../Admin/Admin';
import { AdminProvider } from '../../providers/Admin';
import { NotificationsProvider } from '../../providers/Notifications';
import { FreezePeriodProvider } from '../../providers/FreezePeriod';
import { AcStatsProvider } from '../../providers/AcStats';
import PairingDashboard from '../PairingDashboard/PairingDashboard';
import { useApiError } from '../../providers/ApiErrorProvider';
import AcErrorDialog from '../../components/AcErrorDialog/AcErrorDialog';
import { MyBlockMonthProvider } from '../../providers/MyBlockMonthProvider';
import MyBlockMonth from '../MyBlockMonth/MyBlockMonth';
import { FilterPreferenceProvider } from '../../providers/FilterPreference';
import { SpecialDayOffProvider } from '../../providers/SpecialDayOffProvider';
import SpecialDayOff from '../SpecialDayOff/SpecialDayOff';
import { SdoAdminReportsProvider } from '../../providers/SdoAdminReportsProvider';
import SdoAdminReports from '../SdoAdmin/SdoAdminReports';
import { SdoAdminEligibilityCriteriaProvider } from '../../providers/SdoAdminEligibilityCriteriaProvider';
import SdoAdminEligibilityCriteria from '../SdoAdmin/SdoAdminEligibilityCriteria';
import { SdoApiErrorProvider } from '../../providers/SdoApiErrorProvider';
import SdoAdminReportDetails from '../../components/SdoAdminReportDetails/SdoAdminReportDetails';
import SdoAdminRequestHistory from '../../components/SdoAdminPilotRequestsDetails/SdoAdminPilotRequestsDetails';
import SettingsPage from '../SettingsPage/SettingsPage';
import { SettingsProvider } from '../../providers/SettingsProvider';
import HowToSync from '../SupportPage/HowToSync';
import { SyncToCalendarProvider } from '../../providers/SyncToCalendarProvider';
import { AuthenticationProvider } from '../../providers/AuthenticationProvider';
import AppSessionExpire from '../../components/AppSessionExpire/AppSessionExpire';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HowToSyncProvider } from '../../providers/HowToSyncProvider';
import ExternalAccLoginSuccess from '../ExternalAccLoginSuccess/ExternalAccLoginSuccess';
import GoogleAccessPolicy from '../GoogleAccessPolicy/GoogleAccessPolicy';
import { GoogleAccessPolicyProvider } from '../../providers/GoogleAccessPolicyProvider';
import Acronyms from '../Acronyms/Acronyms';
import Faqs from '../Faqs/Faqs';
import SimSchedule from '../SimSchedule/SimSchedule';
import { SimScheduleProvider } from '../../providers/SimScheduleProvider';

import AdminAppControl from '../AdminAppControl/AdminAppControl';
import { AdminAppControlProvider } from '../../providers/AdminAppControlProvider';

// Maintenance Page imports
import MaintenancePage from '../Maintenance/MaintenancePage';
import { isMaintenanceMode } from '../../helpers/maintenanceHelper';

function App({ outlookAuthCodeChannel }) {
  const user = useAuth();
  const apiError = useApiError();
  const location = useLocation();
  const pathName =
    location.pathname === '/' ? '/' : location.pathname.replace(/\/$/, '');
  const history = useHistory();
  const [isInMaintenanceMode, setIsInMaintenanceMode] = useState(false);
  const [maintenanceCheckComplete, setMaintenanceCheckComplete] =
    useState(false);

  // Check maintenance mode on component mount
  useEffect(() => {
    const checkMaintenanceMode = () => {
      const inMaintenance = isMaintenanceMode();
      setIsInMaintenanceMode(inMaintenance);
      setMaintenanceCheckComplete(true);
    };

    checkMaintenanceMode();
  }, []);

  useEffect(() => {
    if (!user.loading && user.user === undefined) {
      history.push('/login');
    }
  }, [history, user]);

  // Show loading while checking maintenance mode
  if (!maintenanceCheckComplete) {
    return <Loader showText={false} />;
  }

  // Show maintenance page if in maintenance mode
  if (isInMaintenanceMode) {
    return <MaintenancePage />;
  }

  const noFooter = ['/login'];
  const pagesWithFullWidth = [
    '/login',
    '/',
    '/pairing-dashboard',
    '/my-block-month',
    '/sdo-landing',
    '/sdo-admin-ec',
    '/sdo-admin-report-details',
    '/sdo-admin-report',
    '/sdo-admin-request-history',
    '/admin-app-control'
  ];
  const pagesWithFullWidthWhiteBg = [
    '/admin',
    '/pairing-package',
    '/pairing-list',
    '/sim-schedule',
    '/dbm',
    '/settings',
    '/how-to-sync-cal',
    '/ext-login-success',
    '/faqs',
    '/acronyms'
  ];
  const client_id = `${process.env.REACT_APP_GOOGLE_ENT_CLIENT_ID}`;

  return user.loading ? (
    <Loader showText={false} />
  ) : (
    <main
      className={pagesWithFullWidth.includes(pathName) ? 'App bg-grey' : 'App'}
    >
      <AdminAppControlProvider>
        <Navigation />
        <div
          className={
            pathName === '/login'
              ? 'login-page-wrapper'
              : pagesWithFullWidth.includes(pathName) ||
                  pagesWithFullWidthWhiteBg.includes(pathName)
                ? 'full-page-wrapper'
                : pathName === '/google-access-policy'
                  ? 'login-page-wrapper'
                  : 'standard-page-wrapper'
          }
        >
          <AppSessionExpire outlookAuthCodeChannel={outlookAuthCodeChannel} />
          {apiError.error ? <AcErrorDialog errorCode={apiError.error} /> : ''}
          <GoogleOAuthProvider clientId={client_id}>
            <GoogleAccessPolicyProvider>
              <Route
                path="/google-access-policy"
                component={GoogleAccessPolicy}
              />
            </GoogleAccessPolicyProvider>
          </GoogleOAuthProvider>
          <Switch>
            <Route path="/login" component={Login} />
            <ProfileProvider>
              <FreezePeriodProvider>
                <SearchOptions>
                  <NotificationsProvider
                    outlookAuthCodeChannel={outlookAuthCodeChannel}
                  >
                    <AcStatsProvider>
                      <PrivateRoute
                        path="/"
                        exact
                        bindings={[UserRoles.READALL, UserRoles.READ]}
                        component={Home}
                      />
                    </AcStatsProvider>
                    <GeneralPairingProvider>
                      <PairingByIdProvider>
                        <ColumnPresetsProvider>
                          <PairingListProvider>
                            <PrivateRoute
                              path="/pairing-list"
                              bindings={[UserRoles.READALL, UserRoles.READ]}
                              component={PairingList}
                            />
                          </PairingListProvider>
                          <PairingProvider>
                            <FilterPreferenceProvider>
                              <PrivateRoute
                                path="/pairing-package"
                                bindings={[UserRoles.READALL, UserRoles.READ]}
                                component={PairingPackage}
                              />
                            </FilterPreferenceProvider>
                          </PairingProvider>
                        </ColumnPresetsProvider>
                      </PairingByIdProvider>
                    </GeneralPairingProvider>
                    <DBMProvider>
                      <CollectiveAgreementProvider>
                        <PrivateRoute
                          path="/dbm"
                          bindings={[UserRoles.READALL, UserRoles.READ]}
                          component={DBM}
                        />
                      </CollectiveAgreementProvider>
                    </DBMProvider>
                    <AdminProvider>
                      <PrivateRoute
                        path="/admin"
                        bindings={[UserRoles.ADMIN]}
                        component={Admin}
                      />
                    </AdminProvider>
                    <PBIProvider>
                      <PrivateRoute
                        path="/pairing-dashboard"
                        bindings={[UserRoles.READALL, UserRoles.READ]}
                        component={PairingDashboard}
                      />
                    </PBIProvider>
                    <GoogleOAuthProvider clientId={client_id}>
                      <AuthenticationProvider>
                        <SyncToCalendarProvider>
                          <SettingsProvider>
                            <MyBlockMonthProvider>
                              <PrivateRoute
                                path="/my-block-month"
                                bindings={[UserRoles.READALL, UserRoles.READ]}
                                component={MyBlockMonth}
                              />
                              <PrivateRoute
                                path="/settings"
                                bindings={[UserRoles.READALL, UserRoles.READ]}
                                component={SettingsPage}
                              />
                            </MyBlockMonthProvider>
                            <SimScheduleProvider>
                              <PrivateRoute
                                path="/sim-schedule"
                                bindings={[UserRoles.READALL, UserRoles.READ]}
                                component={SimSchedule}
                              />
                            </SimScheduleProvider>
                          </SettingsProvider>
                        </SyncToCalendarProvider>
                      </AuthenticationProvider>
                    </GoogleOAuthProvider>
                    <SdoApiErrorProvider>
                      <SpecialDayOffProvider>
                        <PrivateRoute
                          path="/sdo-landing"
                          bindings={[UserRoles.READALL, UserRoles.READ]}
                          component={SpecialDayOff}
                        />
                        <SdoAdminReportsProvider>
                          <SdoAdminEligibilityCriteriaProvider>
                            <PrivateRoute
                              path="/sdo-admin-report"
                              bindings={[UserRoles.ADMIN]}
                              component={SdoAdminReports}
                            />
                            <PrivateRoute
                              path="/sdo-admin-report-details"
                              bindings={[UserRoles.ADMIN]}
                              component={SdoAdminReportDetails}
                            />
                            <PrivateRoute
                              path="/sdo-admin-ec"
                              bindings={[UserRoles.ADMIN]}
                              component={SdoAdminEligibilityCriteria}
                            />
                            <PrivateRoute
                              path="/admin-app-control"
                              bindings={[UserRoles.ADMIN]}
                              component={AdminAppControl}
                            />
                            <PrivateRoute
                              path="/sdo-admin-request-history"
                              bindings={[UserRoles.ADMIN]}
                              component={SdoAdminRequestHistory}
                            />
                          </SdoAdminEligibilityCriteriaProvider>
                        </SdoAdminReportsProvider>
                      </SpecialDayOffProvider>
                    </SdoApiErrorProvider>
                    <HowToSyncProvider>
                      <PrivateRoute
                        path="/how-to-sync-cal"
                        bindings={[UserRoles.READALL, UserRoles.READ]}
                        component={HowToSync}
                      />
                    </HowToSyncProvider>
                    <PrivateRoute
                      path="/ext-login-success"
                      bindings={[UserRoles.READALL, UserRoles.READ]}
                      component={ExternalAccLoginSuccess}
                    />
                  </NotificationsProvider>
                </SearchOptions>
              </FreezePeriodProvider>
              <PrivateRoute
                path="/faqs"
                bindings={[UserRoles.READALL, UserRoles.READ]}
                component={Faqs}
                exact
              />
              <PrivateRoute
                path="/acronyms"
                bindings={[UserRoles.READALL, UserRoles.READ]}
                component={Acronyms}
              />
            </ProfileProvider>
          </Switch>
        </div>
      </AdminAppControlProvider>
      {noFooter.includes(pathName) ? null : <Footer />}
    </main>
  );
}

export default App;
